import { Injectable } from '@angular/core';
import { SyncProfilerService } from './sync-profiler.service';

interface MetricEntry {
  name: string;
  durationMs: number;
  timestamp: string;
  meta?: Record<string, any>;
}

/**
 * Per-feature profiler instance. Created via FeatureProfiler.for('featureName').
 * Each instance has its own marks, metrics, counters, and localStorage key.
 */
export class ProfilerInstance {
  private readonly logPrefix: string;
  private readonly storageKey: string;
  private readonly marks = new Map<string, number>();
  private metrics: MetricEntry[] = [];
  private readonly counters = new Map<string, number>();
  private sessionStart: number | null = null;

  constructor(
    private readonly featureName: string,
    private readonly syncProfiler: SyncProfilerService
  ) {
    const capitalized = featureName.charAt(0).toUpperCase() + featureName.slice(1);
    this.logPrefix = `[${capitalized}Profiler]`;
    this.storageKey = `${featureName}_profile_log`;
  }

  isEnabled(): boolean {
    return this.syncProfiler.isEnabled();
  }

  mark(name: string): void {
    if (!this.isEnabled()) return;
    this.marks.set(name, performance.now());
  }

  measure(name: string, fromMark: string, meta?: Record<string, any>): void {
    if (!this.isEnabled()) return;
    const start = this.marks.get(fromMark);
    if (start == null) return;
    const durationMs = Number.parseFloat((performance.now() - start).toFixed(2));
    const entry: MetricEntry = {
      name,
      durationMs,
      timestamp: new Date().toISOString(),
      meta,
    };
    this.metrics.push(entry);
    const metaStr = meta ? ` ${JSON.stringify(meta)}` : '';
    console.log(`${this.logPrefix} ${name}: ${durationMs}ms${metaStr}`);
    this.persistEntry(entry);
    this.marks.delete(fromMark);
  }

  timeSync<T>(name: string, fn: () => T, meta?: Record<string, any>): T {
    if (!this.isEnabled()) return fn();
    this.mark(name);
    const result = fn();
    this.measure(name, name, meta);
    return result;
  }

  async timeAsync<T>(name: string, fn: () => Promise<T>, meta?: Record<string, any>): Promise<T> {
    if (!this.isEnabled()) return fn();
    this.mark(name);
    try {
      const result = await fn();
      this.measure(name, name, meta);
      return result;
    } catch (error) {
      this.measure(name, name, { ...meta, failed: true });
      throw error;
    }
  }

  count(name: string, amount = 1): void {
    if (!this.isEnabled()) return;
    this.counters.set(name, (this.counters.get(name) || 0) + amount);
  }

  resetCounters(): void {
    this.counters.clear();
  }

  startSession(): void {
    if (!this.isEnabled()) return;
    this.metrics = [];
    this.counters.clear();
    this.marks.clear();
    this.sessionStart = performance.now();
    console.log(`${this.logPrefix} Session started`);
  }

  endSession(): void {
    if (!this.isEnabled() || this.sessionStart == null) return;
    const totalMs = Math.round(performance.now() - this.sessionStart);
    console.log(`${this.logPrefix} Session ended (${totalMs}ms total)`);
    const report = this.buildReport(totalMs);
    console.log(report);
    this.persistReport(report);
    this.sessionStart = null;
  }

  dump(): string {
    const totalMs = this.sessionStart != null
      ? Math.round(performance.now() - this.sessionStart)
      : null;
    const report = this.buildReport(totalMs);
    console.log(report);
    return report;
  }

  reset(): void {
    this.metrics = [];
    this.counters.clear();
    this.marks.clear();
    this.sessionStart = null;
  }

  private buildReport(sessionDurationMs: number | null): string {
    const lines: string[] = [];
    const timestamp = new Date().toISOString();

    lines.push(`=== ${this.featureName.toUpperCase()} PROFILE ${timestamp} ===`);
    if (sessionDurationMs != null) {
      lines.push(`Session duration: ${sessionDurationMs}ms`);
    }
    lines.push('');

    lines.push('--- Timing ---');
    if (this.metrics.length === 0) {
      lines.push('  (no metrics captured)');
    } else {
      const grouped = new Map<string, MetricEntry[]>();
      for (const m of this.metrics) {
        const list = grouped.get(m.name) || [];
        list.push(m);
        grouped.set(m.name, list);
      }
      for (const [name, entries] of grouped) {
        if (entries.length === 1) {
          const e = entries[0];
          const metaStr = e.meta ? `  ${JSON.stringify(e.meta)}` : '';
          lines.push(`  ${name}: ${e.durationMs}ms${metaStr}`);
        } else {
          const durations = entries.map(e => e.durationMs);
          const avg = Math.round(durations.reduce((a, b) => a + b, 0) / durations.length);
          const min = Math.min(...durations);
          const max = Math.max(...durations);
          lines.push(`  ${name}: avg=${avg}ms  min=${min}ms  max=${max}ms  count=${entries.length}`);
        }
      }
    }
    lines.push('');

    lines.push('--- Counters ---');
    if (this.counters.size === 0) {
      lines.push('  (no counters)');
    } else {
      for (const [name, value] of this.counters) {
        lines.push(`  ${name}: ${value}`);
      }
    }
    lines.push('');

    return lines.join('\n');
  }

  private persistEntry(entry: MetricEntry): void {
    try {
      const existing = localStorage.getItem(this.storageKey) ?? '';
      const metaStr = entry.meta ? ` ${JSON.stringify(entry.meta)}` : '';
      const line = `${entry.timestamp} | ${entry.name}: ${entry.durationMs}ms${metaStr}\n`;
      localStorage.setItem(this.storageKey, existing + line);
    } catch {
      // localStorage may be unavailable
    }
  }

  private persistReport(report: string): void {
    try {
      const existing = localStorage.getItem(this.storageKey) ?? '';
      localStorage.setItem(this.storageKey, existing + report + '\n\n');
    } catch {
      // localStorage may be unavailable
    }
  }
}

/**
 * Factory service for feature profilers. Use FeatureProfiler.for('featureName')
 * to get a profiler instance for any feature.
 *
 * All instances auto-enable when SyncProfilerService is enabled.
 *
 * Usage:
 *   constructor(private profiler: FeatureProfiler) {}
 *   ngOnInit() {
 *     this.profiler.for('activities').mark('list_init');
 *     // ... work ...
 *     this.profiler.for('activities').measure('list_init', 'list_init', { count: 100 });
 *   }
 *
 * Read results per feature:
 *   localStorage.getItem('activities_profile_log')
 *   localStorage.getItem('contacts_profile_log')
 *   localStorage.getItem('presentations_profile_log')
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureProfiler {
  private readonly instances = new Map<string, ProfilerInstance>();

  constructor(private readonly syncProfiler: SyncProfilerService) {}

  /**
   * Get or create a profiler instance for a feature.
   * Instance is cached — safe to call repeatedly.
   */
  for(featureName: string): ProfilerInstance {
    let instance = this.instances.get(featureName);
    if (!instance) {
      instance = new ProfilerInstance(featureName, this.syncProfiler);
      this.instances.set(featureName, instance);
    }
    return instance;
  }
}
